/**
 * V2 screen-reader navigator — V1's Alt+P / F6 "phone menu" idea
 * (components/screen-reader-nav.js), generic over the current page:
 * lists landmarks and headings, number keys or Enter jump focus there.
 */
import { announceToScreenReader } from '../../assets/js/utils.js';

let panel = null;

function collectTargets() {
  const nodes = document.querySelectorAll(
    'main h2, main h3, main section[aria-label], nav[aria-label], header'
  );
  const targets = [];
  for (const node of nodes) {
    const label = node.getAttribute?.('aria-label') || node.textContent?.trim().slice(0, 60);
    if (label && !node.closest('.sr-nav-panel')) targets.push({ node, label });
    if (targets.length >= 9) break;
  }
  return targets;
}

function close() {
  panel?.remove();
  panel = null;
}

function open() {
  close();
  const targets = collectTargets();
  if (!targets.length) return;
  panel = document.createElement('div');
  panel.className = 'sr-nav-panel';
  panel.setAttribute('role', 'menu');
  panel.setAttribute('aria-label', 'Jump to section');
  panel.innerHTML = `
    <p class="sr-nav-title">Jump to section (press a number, Escape to close)</p>
    ${targets.map((t, i) => `
      <button type="button" role="menuitem" data-sr-index="${i}">${i + 1}. ${t.label
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')}</button>`).join('')}`;
  document.body.appendChild(panel);
  panel.querySelector('button')?.focus();

  function jump(index) {
    const target = targets[index];
    if (!target) return;
    close();
    target.node.setAttribute('tabindex', '-1');
    target.node.focus();
    announceToScreenReader(`Jumped to ${target.label}`);
  }

  panel.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-sr-index]');
    if (btn) jump(Number(btn.dataset.srIndex));
  });
  panel.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') return close();
    // role=menu contract: arrows (and Home/End) rove between items
    const items = [...panel.querySelectorAll('[data-sr-index]')];
    const moves = {
      ArrowDown: (i) => (i + 1) % items.length,
      ArrowUp: (i) => (i - 1 + items.length) % items.length,
      Home: () => 0,
      End: () => items.length - 1,
    };
    if (moves[e.key]) {
      e.preventDefault();
      const current = items.indexOf(document.activeElement);
      items[moves[e.key](current < 0 ? 0 : current)]?.focus();
      return;
    }
    const n = Number(e.key);
    if (n >= 1 && n <= targets.length) {
      e.preventDefault();
      jump(n - 1);
    }
  });
}

export function initSrNav() {
  document.addEventListener('keydown', (e) => {
    if ((e.altKey && (e.key === 'p' || e.key === 'P')) || e.key === 'F6') {
      e.preventDefault();
      if (panel) close();
      else open();
    }
  });
}
